import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { reviews } from '@/mocks/reviews';
import { dramas } from '@/mocks/dramas';

export default function ReviewDetail() {
  const { id } = useParams<{ id: string }>();
  const [toast, setToast] = useState('');

  const review = useMemo(() => reviews.find((r) => r.id === id), [id]);
  const drama = useMemo(
    () => (review ? dramas.find((d) => d.id === review.dramaId) : undefined),
    [review],
  );
  const related = useMemo(() => {
    if (!review) return [];
    const sameTag = reviews.filter((r) => r.id !== review.id && r.tags.some((t) => review.tags.includes(t)));
    const rest = reviews.filter((r) => r.id !== review.id && !sameTag.includes(r));
    return [...sameTag, ...rest].slice(0, 3);
  }, [review]);

  const share = () => {
    setToast('分享链接已复制（演示）');
    setTimeout(() => setToast(''), 2200);
  };

  if (!review) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
          <i className="ri-article-line text-5xl text-foreground-600" />
          <h1 className="mt-4 text-xl font-semibold text-foreground-100">该影评暂无内容展示</h1>
          <p className="mt-2 text-sm text-foreground-400">可能文章已下架或链接有误</p>
          <Link to="/reviews" className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            返回影评专题
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/reviews" className="hover:text-primary-400 transition-colors cursor-pointer">影评专题</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300 line-clamp-1">{review.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <article className="lg:col-span-2 min-w-0">
            <div className="flex flex-wrap gap-2 mb-4">
              {review.tags.map((t) => (
                <Link
                  key={t}
                  to="/reviews"
                  className="rounded-full bg-accent-900/50 px-3 py-1 text-xs text-accent-200 hover:bg-accent-800 transition-colors cursor-pointer"
                >
                  {t}
                </Link>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading leading-tight">{review.title}</h1>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-800">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {review.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {review.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                阅读约 {review.readTime}
              </span>
              <button
                type="button"
                onClick={share}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-background-700 px-3 py-1.5 text-xs text-foreground-300 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>

            <div className="mt-6 overflow-hidden rounded-lg bg-background-800 aspect-[16/9]">
              <img src={review.cover} alt={`${review.title} 封面`} title={`${review.title} - 成人短剧影评`} className="w-full h-full object-cover object-top" />
            </div>

            <blockquote className="mt-8 border-l-2 border-primary-500 bg-background-900/50 py-4 pl-5 pr-4 italic text-foreground-200 leading-relaxed">
              {review.quote}
            </blockquote>

            <div className="mt-8 space-y-5">
              {review.content.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base leading-loose text-foreground-300">
                  {paragraph}
                </p>
              ))}
            </div>

            {drama && (
              <div className="mt-10 rounded-lg border border-background-800 bg-background-900/50 p-5 flex items-center gap-4">
                <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-800">
                  <img src={drama.poster} alt={`${drama.title} 海报`} title={`${drama.title} - 成人短剧详情`} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-foreground-500">相关短剧</p>
                  <h3 className="mt-1 text-base font-semibold text-foreground-50">{drama.title}</h3>
                  <p className="mt-1 text-xs text-foreground-400 line-clamp-1">{drama.synopsis}</p>
                </div>
                <Link
                  to={`/detail/${drama.id}`}
                  className="shrink-0 rounded-md border border-background-600 px-4 py-2 text-sm text-foreground-100 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看详情
                </Link>
              </div>
            )}
          </article>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-50 mb-5">
                <span className="block w-1 h-5 rounded-full bg-accent-500" />
                相关文章
              </h2>
              <div className="flex flex-col gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/review/${r.id}`}
                    className="group flex gap-3 rounded-lg border border-background-800 bg-background-900/50 p-3 hover:border-primary-700/70 transition-colors cursor-pointer"
                  >
                    <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-800">
                      <img src={r.cover} alt={`${r.title} 封面`} title={`${r.title} - 成人短剧影评`} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-100 line-clamp-2 group-hover:text-primary-300 transition-colors">
                        {r.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-foreground-500">{r.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-md bg-foreground-50 px-4 py-2.5 text-sm font-medium text-background-950 animate-fade-up">
          <i className="ri-check-line mr-1.5" />
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}